import { logger } from '../utils/logger';
import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    IconButton,
    Box,
    Menu,
    MenuItem,
    useTheme,
    useMediaQuery,
    Typography,
    Avatar,
    Fade,
    Container,
    Stack,
    Divider,
    Tooltip,
} from '@mui/material';
import {
    AccountCircle,
    Business,
    Dashboard,
    Logout,
    Settings,
    KeyboardArrowDown,
    Assessment,
    Person,
    OpenInNew,
    Code,
    Apps,
    Calculate,
    Insights,
    Map,
    Info as InfoIcon,
    Store,
    HowToReg,
    Psychology,
    AccountBalance,
    VpnKey,
    CreditCard,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { OnboardingWizard } from './OnboardingWizard';

const MotionButton = motion(Button);

interface NavMenuItem {
    label: string;
    path?: string;
    items?: { label: string; path: string; icon?: React.ReactNode; external?: boolean }[];
    external?: boolean;
    icon?: React.ReactNode;
}

export const Navbar = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuAnchor, setMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
    const [onboardingOpen, setOnboardingOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isCurrent = (path: string) => path !== '#' && (location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path + '/')));
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmall = useMediaQuery(theme.breakpoints.down('lg'));

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, key: string) => {
        setMenuAnchor({ ...menuAnchor, [key]: event.currentTarget });
    };

    const handleMenuClose = (key: string) => {
        setMenuAnchor({ ...menuAnchor, [key]: null });
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    // Public bar: what we sell, what it costs, how to build on it. Everything else lives in the footer.
    const mainNavItems: NavMenuItem[] = [
        {
            label: 'Product',
            items: [
                { label: 'Tax Calculator', path: '/tax-calculator', icon: <Calculate fontSize="small" /> },
                { label: 'Sales Tax API', path: '/developers', icon: <Code fontSize="small" /> },
                { label: 'Sales Tax Advisor', path: '/products/advisor', icon: <Psychology fontSize="small" /> },
                { label: 'Integrations', path: '/platforms/integrations', icon: <Store fontSize="small" /> },
            ]
        },
        {
            label: 'Pricing',
            path: '/#pricing',
        },
        { label: 'Developers', path: '/developers' },
        // Testing tab - only available on localhost for development
        ...(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? [{
            label: 'Testing', 
            items: [
                { label: 'Tax Testing Results', path: '/tax/testing/results', icon: <Assessment fontSize="small" /> },
                { label: 'Tax API Testing', path: '/tax/insights/testing', icon: <Code fontSize="small" /> },
                { label: 'Tax API Demo', path: '/tax/insights/demo', icon: <Psychology fontSize="small" /> },
                { label: 'Test Results Dashboard', path: '/test-results', icon: <Assessment fontSize="small" /> },
                { label: 'Taxually Filing', path: '/taxually', icon: <Business fontSize="small" /> },
                { label: 'Shopify Testing', path: '/platforms/shopify-integration', icon: <Code fontSize="small" /> },
                { label: 'QuickBooks Integration', path: '/platforms/quickbooks-integration', icon: <AccountBalance fontSize="small" /> },
                { label: 'Reference Engine Testing', path: '/platforms/reference-engine-testing', icon: <Code fontSize="small" /> },
            ]
        }] : []),
    ];

    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isAppAdmin = Boolean(user?.is_app_admin);
    // The Testing list lives in the public nav only on localhost, so define it once here for admins too.
    const testingItems = [
        { label: 'Tax Testing Results', path: '/tax/testing/results', icon: <Assessment fontSize="small" /> },
        { label: 'Tax API Testing', path: '/tax/insights/testing', icon: <Code fontSize="small" /> },
        { label: 'Tax API Demo', path: '/tax/insights/demo', icon: <Psychology fontSize="small" /> },
        { label: 'Test Results Dashboard', path: '/test-results', icon: <Assessment fontSize="small" /> },
        { label: 'Reference Engine Testing', path: '/platforms/reference-engine-testing', icon: <Code fontSize="small" /> },
    ];
    // Signed-in shell: the screens people work in, most used first; everything else sits under More.
    const authenticatedNavItems: { label: string; icon: React.ReactNode; path: string; items?: { label: string; icon?: React.ReactNode; path: string }[] }[] = [
        { label: 'Dashboard', icon: <Dashboard fontSize="small" />, path: '/dashboard' },
        { label: 'Transactions', icon: <Insights fontSize="small" />, path: '/platforms/transactions' },
        { label: 'Nexus', icon: <Map fontSize="small" />, path: '/nexus/dashboard' },
        { label: 'Calculator', icon: <Calculate fontSize="small" />, path: '/tax-calculator' },
        // Filing isn't offered to customers; the page stays reachable for app admins only
        ...(isAppAdmin ? [{ label: 'Filings', icon: <Assessment fontSize="small" />, path: '/tax/filings' }] : []),
        { label: 'Integrations', icon: <Store fontSize="small" />, path: '/platforms/integrations' },
        { label: 'Developers', icon: <VpnKey fontSize="small" />, path: '/developers/console' },
        { label: 'More', icon: <Apps fontSize="small" />, path: '#',
          items: [
            { label: 'Complete Tax View', icon: <Assessment fontSize="small" />, path: '/complete-tax-view' },
            { label: 'Companies', icon: <Business fontSize="small" />, path: '/companies' },
            { label: 'Company Users', icon: <Person fontSize="small" />, path: '/company/users' },
            { label: 'Registrations', icon: <HowToReg fontSize="small" />, path: '/tax/registration' },
            { label: 'Nexus Rules', icon: <InfoIcon fontSize="small" />, path: '/nexus/rules' },
            { label: 'Tax Codes', icon: <Code fontSize="small" />, path: '/tax/codes' },
            { label: 'Reports', icon: <Assessment fontSize="small" />, path: '/reports' },
            { label: 'Sales Tax Advisor', icon: <Psychology fontSize="small" />, path: '/products/advisor' },
            // App admins (server-computed is_app_admin) get the engine comparison and testing pages
            ...(isAppAdmin ? [{ label: 'Tax Engine Comparison', icon: <Code fontSize="small" />, path: '/admin/tax-engine' }, { label: 'Rule updates', icon: <InfoIcon fontSize="small" />, path: '/admin/rule-updates' }, { label: 'Newsletter', icon: <InfoIcon fontSize="small" />, path: '/admin/newsletter' }] : []),
            ...(isLocal ? (mainNavItems.find((m) => m.label === 'Testing')?.items || []) : isAppAdmin ? testingItems : []),
          ]
        },
    ];

    const userMenuItems = [
        { label: 'Profile', icon: <Person />, path: '/profile' },
        { label: 'Settings', icon: <Settings />, path: '/settings' },
        { label: 'Plan and billing', icon: <CreditCard />, path: '/billing' },
        { label: 'API keys and usage', icon: <VpnKey />, path: '/developers/console' },
    ];

    return (
        <AppBar position="fixed" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Container maxWidth="xl">
                <Toolbar disableGutters sx={{ minHeight: '64px' }}>
                    <Box 
                        component={RouterLink}
                        to={user ? '/dashboard' : '/'}
                        aria-label="GoTruTax home"
                        sx={{ 
                            textDecoration: 'none',
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            mr: 3,
                            '&:hover': {
                                '& .logo-text': {
                                    color: theme.palette.primary.main,
                                }
                            }
                        }}
                    >
                        <img 
                            src="/logo.png" 
                            alt="GoTruTax Logo" 
                            style={{ 
                                height: 32, 
                                width: 32,
                                marginRight: theme.spacing(1),
                                cursor: 'pointer'
                            }} 
                        />
                        <Typography
                            variant="h6"
                            component="span"
                            className="app-name logo-text"
                            sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.125rem', transition: 'color 120ms cubic-bezier(0.16, 1, 0.3, 1)' }}
                        >
                            GoTruTax
                        </Typography>
                    </Box>

                    {!isMobile && !user && (
                        <Stack direction="row" spacing={isSmall ? 1 : 3}>
                            {/* Signed-in users reach Developers from Tools; keep the bar from overflowing */}
                            {mainNavItems.map((item) => (
                                <Box key={item.label}>
                                    {item.items ? (
                                        <>
                                            <Button
                                                color="inherit"
                                                onClick={(e) => handleMenuOpen(e, item.label)}
                                                endIcon={<KeyboardArrowDown />}
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 500,
                                                    px: isSmall ? 1 : 2,
                                                    fontSize: isSmall ? '0.875rem' : '1rem',
                                                }}
                                            >
                                                {item.label}
                                            </Button>
                                            <Menu
                                                anchorEl={menuAnchor[item.label] && document.body.contains(menuAnchor[item.label]) ? menuAnchor[item.label] : null}
                                                open={Boolean(menuAnchor[item.label] && document.body.contains(menuAnchor[item.label]))}
                                                onClose={() => handleMenuClose(item.label)}
                                                TransitionComponent={Fade}
                                                PaperProps={{
                                                    sx: {
                                                        mt: 1.5,
                                                        minWidth: 200,
                                                        borderRadius: 1,
                                                    },
                                                }}
                                            >
                                                {item.items.map((subItem) => {
                                                    const linkProps = subItem.external
                                                        ? {
                                                              component: 'a',
                                                              href: subItem.path,
                                                              target: '_blank',
                                                              rel: 'noopener noreferrer',
                                                          }
                                                        : {
                                                              component: RouterLink,
                                                              to: subItem.path,
                                                          };
                                                    return (
                                                        <MenuItem
                                                            key={subItem.path}
                                                            {...linkProps}
                                                            onClick={() => handleMenuClose(item.label)}
                                                            sx={{
                                                                py: 1.2,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            {subItem.icon && (
                                                                <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                                                    {subItem.icon}
                                                                </Box>
                                                            )}
                                                            {subItem.label}
                                                            {subItem.external && <OpenInNew sx={{ ml: 1, fontSize: 14 }} />}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Menu>
                                        </>
                                    ) : (
                                        <Button
                                            color="inherit"
                                            component={item.external ? 'a' : RouterLink}
                                            {...(item.external
                                                ? { href: item.path, target: '_blank', rel: 'noopener noreferrer' }
                                                : { to: item.path! })}
                                            endIcon={item.external ? <OpenInNew sx={{ fontSize: 16 }} /> : undefined}
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 500,
                                                px: isSmall ? 1 : 2,
                                                fontSize: isSmall ? '0.875rem' : '1rem',
                                            }}
                                        >
                                            {item.label}
                                        </Button>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    )}

                    {!user && <Box sx={{ flexGrow: 1 }} />}

                    {isMobile && !user && (
                        <>
                            <IconButton aria-label="Open menu" data-testid="nav-mobile-menu" aria-haspopup="menu" onClick={(e) => handleMenuOpen(e, 'public-mobile')} sx={{ mr: 0.5 }}>
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchor['public-mobile'] && document.body.contains(menuAnchor['public-mobile']) ? menuAnchor['public-mobile'] : null}
                                open={Boolean(menuAnchor['public-mobile'] && document.body.contains(menuAnchor['public-mobile']))}
                                onClose={() => handleMenuClose('public-mobile')}
                                PaperProps={{ sx: { mt: 1, minWidth: 220 } }}
                            >
                                {mainNavItems.flatMap((item) => (item.items ? item.items : [{ label: item.label, path: item.path || '/' }])).map((entry) => (
                                    <MenuItem key={entry.label} component={RouterLink} to={entry.path} onClick={() => handleMenuClose('public-mobile')} sx={{ py: 1.2 }}>
                                        {entry.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}

                    {user ? (
                        <>
                            {!isMobile && (
                                <Stack direction="row" spacing={isSmall ? 0 : 0.5} sx={{ mr: 'auto', ml: 1 }} component="nav" aria-label="App" data-testid="nav-app">
                                    {authenticatedNavItems.map((item) => (
                                        item.items ? (
                                            <Box key={item.label}>
                                                <Tooltip title={item.label}>
                                                    <Button
                                                        color="inherit"
                                                        onClick={(e) => handleMenuOpen(e, item.label)}
                                                        endIcon={<KeyboardArrowDown />}
                                                        aria-haspopup="menu"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontWeight: 500,
                                                            px: isSmall ? 1 : 1.5,
                                                            fontSize: '0.9375rem',
                                                            '&:hover': { color: 'text.primary' },
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Button>
                                                </Tooltip>
                                                <Menu
                                                    anchorEl={menuAnchor[item.label] && document.body.contains(menuAnchor[item.label]) ? menuAnchor[item.label] : null}
                                                    open={Boolean(menuAnchor[item.label] && document.body.contains(menuAnchor[item.label]))}
                                                    onClose={() => handleMenuClose(item.label)}
                                                    TransitionComponent={Fade}
                                                    PaperProps={{
                                                        sx: {
                                                            mt: 1.5,
                                                            minWidth: 180,
                                                            borderRadius: 1,
                                                        },
                                                    }}
                                                >
                                                    {item.items.map((subItem) => (
                                                        <MenuItem
                                                            key={subItem.path}
                                                            component={RouterLink}
                                                            to={subItem.path}
                                                            onClick={() => handleMenuClose(item.label)}
                                                            sx={{ py: 1.2 }}
                                                        >
                                                            {subItem.icon && (
                                                                <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                                                    {subItem.icon}
                                                                </Box>
                                                            )}
                                                            {subItem.label}
                                                        </MenuItem>
                                                    ))}
                                                </Menu>
                                            </Box>
                                        ) : (
                                            <Button
                                                key={item.path}
                                                color="inherit"
                                                component={RouterLink}
                                                to={item.path}
                                                className="gt-nav-link"
                                                aria-current={isCurrent(item.path) ? 'page' : undefined}
                                                sx={{
                                                    color: isCurrent(item.path) ? 'text.primary' : 'text.secondary',
                                                    fontWeight: 500,
                                                    px: isSmall ? 1 : 1.5,
                                                    fontSize: '0.9375rem',
                                                    '&:hover': { color: 'text.primary' },
                                                }}
                                            >
                                                {item.label}
                                            </Button>
                                        )
                                    ))}
                                </Stack>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                                {!isMobile && (
                                    <Button component={RouterLink} to="/billing" variant="outlined" size="small" sx={{ mr: 0.5 }}>
                                        Plans
                                    </Button>
                                )}
                                <Tooltip title={user.full_name || "Account"}>
                                    <IconButton
                                        size="medium"
                                        onClick={handleMenu}
                                        aria-label="Account menu"
                                        data-testid="nav-account-menu"
                                        sx={{ p: 0.5, borderRadius: '50%' }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: 'primary.main',
                                                color: 'primary.contrastText',
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {user.full_name?.charAt(0) || <AccountCircle />}
                                        </Avatar>
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Menu
                                anchorEl={anchorEl && document.body.contains(anchorEl) ? anchorEl : null}
                                open={Boolean(anchorEl && document.body.contains(anchorEl))}
                                onClose={handleClose}
                                TransitionComponent={Fade}
                                PaperProps={{
                                    sx: {
                                        mt: 1.5,
                                        minWidth: 220,
                                        borderRadius: 1,
                                    },
                                }}
                            >
                                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {user.full_name || 'User'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {user.email}
                                    </Typography>
                                </Box>

                                {isMobile && (
                                    <>
                                        {authenticatedNavItems.map((item) => (
                                            item.items ? (
                                                <React.Fragment key={item.label}>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            px: 2, 
                                                            py: 0.5, 
                                                            display: 'block', 
                                                            bgcolor: 'background.default',
                                                            color: 'text.secondary',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                    {item.items.map((subItem) => (
                                                        <MenuItem
                                                            key={subItem.path}
                                                            onClick={() => {
                                                                handleClose();
                                                                navigate(subItem.path);
                                                            }}
                                                            sx={{ py: 1.2 }}
                                                        >
                                                            {subItem.icon && (
                                                                <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                                                    {subItem.icon}
                                                                </Box>
                                                            )}
                                                            {subItem.label}
                                                        </MenuItem>
                                                    ))}
                                                </React.Fragment>
                                            ) : (
                                                <MenuItem
                                                    key={item.path}
                                                    onClick={() => {
                                                        handleClose();
                                                        navigate(item.path);
                                                    }}
                                                    sx={{ py: 1.2 }}
                                                >
                                                    {item.icon && (
                                                        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                                            {item.icon}
                                                        </Box>
                                                    )}
                                                    {item.label}
                                                </MenuItem>
                                            )
                                        ))}
                                        <Divider sx={{ my: 1 }} />
                                    </>
                                )}

                                {userMenuItems.map((item) => (
                                    <MenuItem
                                        key={item.path}
                                        onClick={() => {
                                            handleClose();
                                            navigate(item.path);
                                        }}
                                        sx={{
                                            py: 1.2,
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                        }}
                                    >
                                        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                            {item.icon}
                                        </Box>
                                        {item.label}
                                    </MenuItem>
                                ))}

                                <Divider sx={{ my: 1 }} />
                                
                                <MenuItem 
                                    onClick={handleLogout}
                                    sx={{
                                        py: 1.2,
                                        color: 'error.main',
                                        '&:hover': {
                                            backgroundColor: 'error.light',
                                            color: 'error.contrastText',
                                        },
                                    }}
                                >
                                    <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                        <Logout />
                                    </Box>
                                    Logout
                                </MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 } }}>
                                <MotionButton
                                    variant="outlined"
                                    onClick={() => navigate('/login')}
                                    data-testid="nav-sign-in"
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.98 }}
                                    sx={{ fontWeight: 500, whiteSpace: 'nowrap', px: { xs: 1.5, sm: 2 } }}
                                >
                                    Login
                                </MotionButton>
                                <MotionButton
                                    variant="contained"
                                    onClick={() => setOnboardingOpen(true)}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.98 }}
                                    sx={{ fontWeight: 500, whiteSpace: 'nowrap', px: { xs: 1.5, sm: 2 } }}
                                >
                                    Get Started
                                </MotionButton>
                            </Box>
                        </>
                    )}
                </Toolbar>
            </Container>
            <OnboardingWizard
                open={onboardingOpen}
                onClose={() => setOnboardingOpen(false)}
                onComplete={() => setOnboardingOpen(false)}
            />
        </AppBar>
    );
}; 
